import { AccountBalanceWallet, Payment, PowerSettingsNew, Search, SpaceDashboard, Wallet as WalletIcon } from "@mui/icons-material";
import { AppBar, Box, Button, Card, CardContent, CircularProgress, Drawer, FormControl, FormHelperText, Grid2, InputLabel, List, ListItem, ListItemButton, ListItemIcon, ListItemText, MenuItem, Select, styled, Table, TableBody, TableCell, TableHead, TablePagination, TableRow, TableSortLabel, TextField, Toolbar, Typography } from "@mui/material";
import { motion } from "framer-motion";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import logo from "../assets/logo-only-light.png";
import { BASE_URL } from "../url";
import error_codes from "../utils/rbgi-error-codes";
import theme from "./theme";
import Wallet from "./Wallet";

const StyledCard = styled(Card)({
    borderRadius: "0",
    width: "100%",
});
  
const StyledButton = styled(Button)({
    margin: "8px",
    textTransform: "none",
    borderRadius: "0",
    boxShadow: "none"
});

const StyledDrawer = styled(Drawer)({
    width: "214px",
    height: "100%",
    [`& .MuiDrawer-paper`]: {position: 'relative'},
})

const FlatTextField = styled(TextField)({
    ".MuiInputBase-root": {
        borderRadius: "0",
    }
});

const FlatSelectField = styled(Select)({
    ".MuiInputBase-root": {
        borderRadius: "0",
    }
});

const GradientAppBar = styled(AppBar)({
    zIndex: theme.zIndex.drawer + 1,
    background: `linear-gradient(to right, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
})

const DashboardBody = styled(Grid2)({
    backgroundColor: "#f8f9fa",
    overflow       : "auto",
    minHeight      : "calc(100vh - 64px)",
    maxHeight      : "calc(100vh - 64px)",
    marginTop      : "64px"
})

const StyledListItemButton = styled(ListItemButton)({
    [`&.Mui-selected .MuiListItemText-root`]: {
        color: theme.palette.secondary.main
    }
})

const StyledTableCell = styled(TableCell)({
    whiteSpace: 'nowrap'
})

const statusColors: any = {
    SUCCESS: 'success',
    FAILED : 'error',
    PENDING: 'warning',
    EXPIRED: 'textSecondary'
}

const Dashboard = () => {
    const navigate = useNavigate();
    const [data, setData] = useState<any[]>([]);
    const [paymentSummary, setPaymentSummary] = useState({
        TOTAL  : 0,
        COUNT  : 0,
        SUCCESS: 0,
        PENDING: 0,
        CLOSED : 0,
        FAILED : 0,
        TRX_MIN: 0,
    });
    const [disburseSummary, setDisburseSummary] = useState({
        TOTAL  : 0,
        COUNT  : 0,
        SUCCESS: 0,
        PENDING: 0,
        CLOSED : 0,
        FAILED : 0,
        BALANCE: 0,
    });

    const [transaction_type, setTransactionType] = useState('');
    const [openWallet, setOpenWallet] = useState(false);

    const [date, setDate] = useState(moment().utcOffset('+0800').format('YYYY-MM-DD'));
    const [cutoff, setCutoff] = useState<'All' | '12AM' | '8AM' | '4PM'>('All');

    const [search, setSearch] = useState("");
    const [status, setStatus] = useState("All");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(100);
    const [order, setOrder] = useState<"asc" | "desc">("desc");
    const [orderBy, setOrderBy] = useState("created_at")

    const [downloading, setDownloading] = useState(false);
    const [loading, setLoading] = useState(true);
    const [summaryLoading, setSummaryLoading] = useState(true);
    const [reset, setReset] = useState(false);
    const mounted = useRef(false);

    const [summary, setSummary] = useState<any>({})

    let cutoffs = {
        "12AM": {start_time: '00:00:00', end_time: '07:59:59.999999'},
        "8AM" : {start_time: '08:00:00', end_time: '15:59:59.999999'},
        "4PM" : {start_time: '16:00:00', end_time: '23:59:59.999999'},
    }
    const descendingComparator = (a: any, b: any, orderBy: string) => {
        if (b[orderBy] < a[orderBy]) {
          return -1;
        }
        if (b[orderBy] > a[orderBy]) {
          return 1;
        }
        return 0;
      }
      
    const getComparator = (order: "asc" | "desc", orderBy: string) => {
        return order === 'desc'
            ? (a: any, b: any) => descendingComparator(a, b, orderBy)
            : (a: any, b: any) => -descendingComparator(a, b, orderBy);
    }

    const download = () => {
        const token = JSON.parse(localStorage.getItem("token") || '{}');
        if (token && transaction_type) {
            setDownloading(true);
            let time = cutoff == 'All'? '': `start_time=${cutoffs[cutoff].start_time}&end_time=${cutoffs[cutoff].end_time}`;
            fetch(`${window.location.protocol}//${BASE_URL}/dashboard/transactions/${transaction_type}/download?status=${status == 'All'? '': status}&start=${date}&end=${date}&${time}`, {
                headers: {
                    Authorization: `Bearer ${token.access_token}`
                }
            })
            .then(res => res.status != 200? navigate('/login'): res)
            .then((res: any) => res.text())
            .then(url => {
                if (url) {
                    console.log(url)
                    let link = document.createElement('a');
                    link.href = url.replaceAll('"', '');
                    // link.download = `${{
                    //     payment: 'Payments',
                    //     'fund-transfer': 'Disbursements',
                    // }[transaction_type]} Transactions ${moment(date).format('MMDDYY')} ${{
                    //     'All' : '12am-12am',
                    //     '12AM': '12am-8am',
                    //     '8AM' : '8am-4pm',
                    //     '4PM' : '4pm-12am'
                    // }[cutoff]}.xlsx`;
                    link.target = "_blank";
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                }
            })
            .finally(() => setDownloading(false));
        }
    }

    const fetchSummary = async (token: any, type: string) => {
        setSummaryLoading(true);
        let time = cutoff == 'All'? '': `start_time=${cutoffs[cutoff].start_time}&end_time=${cutoffs[cutoff].end_time}`;
        return await fetch(
            `${window.location.protocol}//${BASE_URL}/dashboard/success-rate/${type}?start=${date}&end=${date}&${time}`,
            {
                headers: {
                    Authorization: `Bearer ${token.access_token}`
                }
            }
        )
        .then(res => res.json())
        .finally(() => setSummaryLoading(false))
    }

    const fetchTransactions = async (token: any) => {
        setData([]);
        let time = cutoff == 'All'? '': `start_time=${cutoffs[cutoff].start_time}&end_time=${cutoffs[cutoff].end_time}`;
        fetch(`${window.location.protocol}//${BASE_URL}/dashboard/transactions/${transaction_type}?status=${status == 'All'? '': status}&start=${date}&end=${date}&page=${page}&limit=${rowsPerPage}&search=${search || ''}&${time}`, {
            headers: {
                Authorization: `Bearer ${token.access_token}`
            }
        })
        .then(res => res.status == 200? res.json(): navigate('/login'))
        .then(res => setData(res))
        .finally(() => setLoading(false))
    }

    const fetchWalletSummary = async (token: any) => {
        setSummaryLoading(true);
        return await fetch(
            `${window.location.protocol}//${BASE_URL}/dashboard/account/wallet/orionx-dashboard`,
            {
                headers: {
                    Authorization: `Bearer ${token.access_token}`
                }
            }
        )
        .then(res => res.json())
        .then(res => setSummary(res))
    }

    const loadData = async () => {
        setLoading(true);
        const token = JSON.parse(localStorage.getItem("token") || '{}');
        if (token) {
            fetchWalletSummary(token)
            transaction_type && fetchTransactions(token)
            switch (transaction_type) {
                case 'payment'      : await fetchSummary(token, 'payment').then(summary => setPaymentSummary(summary)); break;
                case 'fund-transfer': await fetchSummary(token, 'fund-transfer').then(summary => setDisburseSummary(summary)); break;
                default:
                    await fetchSummary(token, 'payment').then(summary => setPaymentSummary(summary));
                    await fetchSummary(token, 'fund-transfer').then(summary => setDisburseSummary(summary));
                    break;
            }
        }
    }

    useEffect(() => {
        if (mounted.current) {
            loadData()
        } else {
            mounted.current = true;
        }
    }, [reset])

    useEffect(() => {
        setPaymentSummary({
            TOTAL  : 0,
            COUNT  : 0,
            SUCCESS: 0,
            PENDING: 0,
            CLOSED : 0,
            FAILED : 0,
            TRX_MIN: 0,
        })
        setDisburseSummary({
            TOTAL  : 0,
            COUNT  : 0,
            SUCCESS: 0,
            PENDING: 0,
            CLOSED : 0,
            FAILED : 0,
            BALANCE: 0,
        })
        setStatus('All')
        setDate(moment().utcOffset('+0800').format('YYYY-MM-DD'))
        setPage(0)
        setOrder('desc')
        setOrderBy('created_at')
        setReset(!reset)
    }, [transaction_type])

    // Debounce logic using useEffect
    useEffect(() => {
        const handler = setTimeout(() => {
            if (mounted.current && transaction_type) {
                const token = JSON.parse(localStorage.getItem("token") || '{}');
                fetchTransactions(token)
            }
        }, 500); // 500ms debounce delay

        return () => {
            clearTimeout(handler); // Clear timeout on query change
        };
    }, [search]);

    useEffect(() => {
        if (mounted.current && transaction_type) {
            const token = JSON.parse(localStorage.getItem("token") || '{}');
            fetchTransactions(token)
        }
    }, [page])

    useEffect(() => {
        if (mounted.current) {
            setPage(0);
        }
    }, [rowsPerPage])

    return <>
        <GradientAppBar position="fixed">
            <motion.div
                initial    = {{ opacity: 0, x: -20 }}
                animate    = {{ opacity: 1, x: 0 }}
                transition = {{ duration: 1 }}
            >
                <Toolbar>
                    <Typography noWrap component="div" sx={{margin: '0 8px'}}>
                        <img src={logo} width="25px"/>
                    </Typography>
                    <Typography noWrap component="div" fontSize="18px" sx={{margin: '0 8px'}}>
                        Dashboard Portal
                    </Typography>
                </Toolbar>
            </motion.div>
        </GradientAppBar>
        <DashboardBody container wrap="nowrap">
            <Grid2 sx={{minWidth: "214px"}}>
                <StyledDrawer variant="permanent" anchor="left">
                    <List sx={{height: '100%', overflowY: 'hidden'}}>
                        <motion.div
                            initial    = {{ opacity: 0, x: -20 }}
                            animate    = {{ opacity: 1, x: 0 }}
                            transition = {{ duration: 1 }}
                        >
                            <ListItem disablePadding>
                                <StyledListItemButton selected={!transaction_type && !openWallet} onClick={() => {
                                    setTransactionType('')
                                    setOpenWallet(false)
                                }}>
                                    <ListItemIcon>
                                        <SpaceDashboard color="primary"/>
                                    </ListItemIcon>
                                    <ListItemText primary="Dashboard" />
                                </StyledListItemButton>
                            </ListItem>
                        </motion.div>
                        <motion.div
                            initial    = {{ opacity: 0, x: -20 }}
                            animate    = {{ opacity: 1, x: 0 }}
                            transition = {{ duration: 1.25 }}
                        >
                            <ListItem disablePadding>
                                <StyledListItemButton selected={transaction_type == 'payment'} onClick={() => setTransactionType('payment')}>
                                    <ListItemIcon>
                                        <Payment color="primary"/>
                                    </ListItemIcon>
                                    <ListItemText primary="Payments" />
                                </StyledListItemButton>
                            </ListItem>
                        </motion.div>
                        <motion.div
                            initial    = {{ opacity: 0, x: -20 }}
                            animate    = {{ opacity: 1, x: 0 }}
                            transition = {{ duration: 1.5 }}
                        >
                            <ListItem disablePadding>
                                <StyledListItemButton selected={transaction_type == 'fund-transfer'} onClick={() => setTransactionType('fund-transfer')}>
                                    <ListItemIcon>
                                        <AccountBalanceWallet color="primary"/>
                                    </ListItemIcon>
                                    <ListItemText primary="Disbursements" />
                                </StyledListItemButton>
                            </ListItem>
                        </motion.div>
                        <motion.div
                            initial    = {{ opacity: 0, x: -20 }}
                            animate    = {{ opacity: 1, x: 0 }}
                            transition = {{ duration: 1.5 }}
                        >
                            <ListItem disablePadding>
                                <StyledListItemButton selected={openWallet && !transaction_type} onClick={() => {
                                    setTransactionType('')
                                    setOpenWallet(true)
                                }}>
                                    <ListItemIcon>
                                        <WalletIcon color="primary"/>
                                    </ListItemIcon>
                                    <ListItemText primary="Wallet" />
                                </StyledListItemButton>
                            </ListItem>
                        </motion.div>
                        <Box position="absolute" bottom={0} width="100%!important">
                            <motion.div
                                initial    = {{ opacity: 0, y: 20 }}
                                animate    = {{ opacity: 1, y: 0 }}
                                transition = {{ duration: 1 }}
                            >
                                <ListItem disablePadding>
                                    <StyledListItemButton onClick={() => {
                                        localStorage.removeItem('token')
                                        navigate('/login')
                                    }}>
                                        <ListItemIcon>
                                            <PowerSettingsNew color="error"/>
                                        </ListItemIcon>
                                        <ListItemText primary="Logout" />
                                    </StyledListItemButton>
                                </ListItem>
                            </motion.div>
                        </Box>
                    </List>
                </StyledDrawer>
            </Grid2>
            <Grid2 size="grow" style={{padding: "24px", overflow: 'auto'}} columns={6}>
                {transaction_type == 'payment' &&
                    <Typography variant="h5" sx={{marginBottom: '1rem'}}>
                        Payments
                    </Typography>
                }
                {transaction_type == 'fund-transfer' &&
                    <Typography variant="h5" sx={{marginBottom: '1rem'}}>
                        Disbursements
                    </Typography>
                }
                {transaction_type && <StyledCard sx={{marginBottom: '1rem', minWidth: '400px'}}>
                    <CardContent>
                        <Grid2 container spacing={2}>
                            <Grid2 size={{xs: 12, md: 2}}>
                                <motion.div
                                    initial    = {{ opacity: 0, x: -20 }}
                                    animate    = {{ opacity: 1, x: 0 }}
                                    transition = {{ duration: 0.75 }}
                                >
                                    <FlatTextField fullWidth
                                        size      = "small"
                                        variant   = "outlined"
                                        label     = "Date"
                                        type      = "date"
                                        value     = {date}
                                        slotProps = {{inputLabel: {shrink: true}, input: {inputProps: {max: moment().utcOffset('+0800').format('YYYY-MM-DD')}}}}
                                        onChange  = {e => setDate(e.target.value)}
                                    />
                                </motion.div>
                            </Grid2>
                            <Grid2 size={{xs: 12, md: 2}}>
                                <motion.div
                                    initial    = {{ opacity: 0, x: -20 }}
                                    animate    = {{ opacity: 1, x: 0 }}
                                    transition = {{ duration: 0.75 }}
                                >
                                    <FormControl fullWidth>
                                        <InputLabel id="cutoff-label">Cutoff</InputLabel>
                                        <FlatSelectField fullWidth
                                            id        = "cutoff"
                                            labelId   = "cutoff-label"
                                            label     = "Cutoff"
                                            size      = "small"
                                            value     = {cutoff}
                                            onChange = {e => setCutoff(e.target.value as any)}
                                        >
                                            <MenuItem value="All"><em>All</em></MenuItem>
                                            <MenuItem value="12AM">
                                                <Typography color="textSecondary">
                                                    12AM - 8AM
                                                </Typography>
                                            </MenuItem>
                                            <MenuItem value="8AM">
                                                <Typography color="textSecondary">
                                                    8AM - 4PM
                                                </Typography>
                                            </MenuItem>
                                            <MenuItem value="4PM">
                                                <Typography color="textSecondary">
                                                    4PM - 12AM
                                                </Typography>
                                            </MenuItem>
                                        </FlatSelectField>
                                    </FormControl>
                                </motion.div>
                            </Grid2>
                            <Grid2 size={{xs: 12, md: 2}}>
                                <motion.div
                                    initial    = {{ opacity: 0, x: -20 }}
                                    animate    = {{ opacity: 1, x: 0 }}
                                    transition = {{ duration: 0.75 }}
                                >
                                    <FormControl fullWidth>
                                        <InputLabel id="status-label">Status</InputLabel>
                                        <FlatSelectField fullWidth
                                            id        = "status"
                                            labelId   = "status-label"
                                            label     = "Status"
                                            size      = "small"
                                            value     = {status}
                                            onChange = {e => setStatus(e.target.value as string)}
                                        >
                                            <MenuItem value="All"><em>All</em></MenuItem>
                                            <MenuItem value="SUCCESS">
                                                <Typography color="success">
                                                    Success
                                                </Typography>
                                            </MenuItem>
                                            <MenuItem value="PENDING">
                                                <Typography color="warning">
                                                    Pending
                                                </Typography>
                                            </MenuItem>
                                            <MenuItem value="CLOSED">
                                                <Typography color="textSecondary">
                                                    Closed
                                                </Typography>
                                            </MenuItem>
                                            <MenuItem value="FAILED">
                                                <Typography color="error">
                                                    Failed
                                                </Typography>
                                            </MenuItem>
                                        </FlatSelectField>
                                    </FormControl>
                                </motion.div>
                            </Grid2>
                            <Grid2 size={{xs: 12, md: 2}}>
                                <motion.div
                                    initial    = {{ opacity: 0, x: -20 }}
                                    animate    = {{ opacity: 1, x: 0 }}
                                    transition = {{ duration: 1.5 }}
                                >
                                    <StyledButton
                                        sx       = {{marginTop: '0', marginBottom: '0'}}
                                        variant  = "contained"
                                        color    = "primary"
                                        onClick  = {e => loadData()}
                                        disabled = {loading}
                                    >
                                        {loading? <CircularProgress size="24px"/>: "Apply"}
                                    </StyledButton>
                                </motion.div>
                            </Grid2>
                        </Grid2>
                    </CardContent>
                </StyledCard>}
                {
                    transaction_type == 'payment'? <Summary summary={paymentSummary} loading={summaryLoading} type='payment'/>:
                    transaction_type == 'fund-transfer'? <Summary summary={disburseSummary} loading={summaryLoading} type='fund-transfer'/>:
                    !openWallet && <>
                        <Typography variant="h5">
                            Payments
                        </Typography>
                        <Summary summary={paymentSummary} loading={summaryLoading} type='payment'/>
                        <Typography variant="h5">
                            Disbursements
                        </Typography>
                        <Summary summary={disburseSummary} loading={summaryLoading} type='fund-transfer'/>
                    </>
                }
                {transaction_type && <StyledCard sx={{margin: '0.5rem 0', minWidth: '400px'}}>
                    <CardContent>
                        <FormControl sx={{margin: '0.5rem'}}>
                            <FlatTextField
                                size      = "small"
                                variant   = "outlined"
                                label     = "Search"
                                value     = {search}
                                slotProps = {{inputLabel: {shrink: true}, input: {startAdornment: <Search color="disabled"/>}}}
                                onChange  = {e => setSearch(e.target.value)}
                            />
                            <FormHelperText>ID / Reference / QRPH No. / Instapay Reference</FormHelperText>
                        </FormControl>
                    </CardContent>
                    <CardContent sx={{overflow: 'auto', maxHeight: '375px', paddingTop: 0}}>
                        <Table stickyHeader size="small">
                            <TableHead sx={{width: '100%'}}>
                                <TableRow>
                                    <StyledTableCell sortDirection={orderBy === "transaction_id" ? order : false}>
                                        <TableSortLabel
                                            active    = {orderBy === "transaction_id"}
                                            direction = {orderBy === "transaction_id" ? order : 'asc'}
                                            // onClick   = {() => orderData("transaction_id")}
                                        >
                                            Transaction ID
                                        </TableSortLabel>
                                    </StyledTableCell>
                                    <StyledTableCell sortDirection={orderBy === "reference_id" ? order : false}>
                                        <TableSortLabel
                                            active    = {orderBy === "reference_id"}
                                            direction = {orderBy === "reference_id" ? order : 'asc'}
                                            // onClick   = {() => orderData("reference_id")}
                                        >
                                            Reference
                                        </TableSortLabel>
                                    </StyledTableCell>
                                    <StyledTableCell sortDirection={orderBy === "instapay_reference" ? order : false}>
                                        <TableSortLabel
                                            active    = {orderBy === "instapay_reference"}
                                            direction = {orderBy === "instapay_reference" ? order : 'asc'}
                                            // onClick   = {() => orderData("instapay_reference")}
                                        >
                                            QRPH / Instapay Ref.
                                        </TableSortLabel>
                                    </StyledTableCell>
                                    <StyledTableCell sortDirection={orderBy === "amount" ? order : false}>
                                        <TableSortLabel
                                            active    = {orderBy === "amount"}
                                            direction = {orderBy === "amount" ? order : 'asc'}
                                            // onClick   = {() => orderData("amount")}
                                        >
                                            Transaction Amount
                                        </TableSortLabel>
                                    </StyledTableCell>
                                    <StyledTableCell sortDirection={orderBy === "amount" ? order : false}>
                                        <TableSortLabel
                                            active    = {orderBy === "amount"}
                                            direction = {orderBy === "amount" ? order : 'asc'}
                                            // onClick   = {() => orderData("amount")}
                                        >
                                            Amount
                                        </TableSortLabel>
                                    </StyledTableCell>
                                    <StyledTableCell sortDirection={orderBy === "fees" ? order : false}>
                                        <TableSortLabel
                                            active    = {orderBy === "fees"}
                                            direction = {orderBy === "fees" ? order : 'asc'}
                                            // onClick   = {() => orderData("fees")}
                                        >
                                            Fees
                                        </TableSortLabel>
                                    </StyledTableCell>
                                    <StyledTableCell sortDirection={orderBy === "status" ? order : false}>
                                        <TableSortLabel
                                            active    = {orderBy === "status"}
                                            direction = {orderBy === "status" ? order : 'asc'}
                                            // onClick   = {() => orderData("status")}
                                        >
                                            Status
                                        </TableSortLabel>
                                    </StyledTableCell>
                                    <StyledTableCell sortDirection={orderBy === "description" ? order : false}>
                                        <TableSortLabel
                                            active    = {orderBy === "description"}
                                            direction = {orderBy === "description" ? order : 'asc'}
                                            // onClick   = {() => orderData("description")}
                                        >
                                            Description
                                        </TableSortLabel>
                                    </StyledTableCell>
                                    <StyledTableCell sortDirection={orderBy === "created_at" ? order : false}>
                                        <TableSortLabel
                                            active    = {orderBy === "created_at"}
                                            direction = {orderBy === "created_at" ? order : 'asc'}
                                            // onClick   = {() => orderData("created_at")}
                                        >
                                            Created
                                        </TableSortLabel>
                                    </StyledTableCell>
                                    <StyledTableCell sortDirection={orderBy === "paid_at" ? order : false}>
                                        <TableSortLabel
                                            active    = {orderBy === "paid_at"}
                                            direction = {orderBy === "paid_at" ? order : 'asc'}
                                            // onClick   = {() => orderData("paid_at")}
                                        >
                                            Paid
                                        </TableSortLabel>
                                    </StyledTableCell>
                                    <StyledTableCell>{/*Resend Button Column*/}</StyledTableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody sx={{width: '100%'}}>
                            {data?.map((payment, index) => (
                                <TableRow key={index}>
                                    <StyledTableCell>{payment.transaction_id || '-'}</StyledTableCell>
                                    <StyledTableCell>{payment.reference_id || '-'}</StyledTableCell>
                                    <StyledTableCell>{payment.instapay_reference || '-'}</StyledTableCell>
                                    <StyledTableCell>₱{(+(payment.amount || 0)).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}) || '-'}</StyledTableCell>
                                    <StyledTableCell>₱{((!transaction_type? 0: {
                                        'payment'      : +(payment.amount || 0) - (payment.fees || +(payment.amount || 0)*(summary?.fees?.CASHIN?.fee_value || 0)/100),
                                        'fund-transfer': +(payment.amount || 0) + (payment.fees || (summary?.fees?.CASHOUT?.fee_value || 0))
                                    }[transaction_type]) || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}) || '-'}</StyledTableCell>
                                    <StyledTableCell>₱{(+(payment.fees || 0)).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}) || '-'}</StyledTableCell>
                                    <StyledTableCell>
                                        <Typography color={statusColors[payment.status]} textTransform="capitalize">
                                            {payment.status?.toLowerCase() || '-'}
                                        </Typography>
                                    </StyledTableCell>
                                    <StyledTableCell>{error_codes[payment.error || ''] || payment.error || '-'}</StyledTableCell>
                                    <StyledTableCell>{payment.created_at? moment(payment.created_at).utcOffset('+0800').format('YYYY-MM-DD HH:mm:ss'): '-'}</StyledTableCell>
                                    <StyledTableCell>{payment.paid_at? moment(payment.paid_at).utcOffset('+0800').format('YYYY-MM-DD HH:mm:ss'): '-'}</StyledTableCell>
                                    <StyledTableCell>
                                        <ResendButton id={payment.transaction_id}/>
                                        {
                                            payment.type == 'FUND_TRANSFER' &&
                                            <ViewButton id={payment.transaction_id} amount={payment.amount} status={payment.status}/>
                                        }
                                    </StyledTableCell>
                                    </TableRow>
                            ))}
                            </TableBody>
                        </Table>
                    </CardContent>
                    <CardContent>
                        <StyledButton variant="contained" sx={{float: 'left', minWidth: '132.32px', minHeight: '33px'}} onClick={download} disabled={downloading}>
                            {downloading? <CircularProgress size="24px"/>: "Download (.xlsx)"}
                        </StyledButton>
                        <TablePagination
                            component    = "div"
                            count        = {
                                ((transaction_type == 'payment'? paymentSummary: disburseSummary) as any)[(status == 'All'? 'COUNT': status.toUpperCase())]
                            }
                            page         = {page}
                            onPageChange = {(e: any, page: number) => setPage(page == -1? 0: page)}
                            rowsPerPage  = {rowsPerPage}
                            rowsPerPageOptions={[
                                {value: 100, label: '100'},
                                {value: 500, label: '500'},
                                {value: 1000, label: '1,000'}
                            ]}
                            onRowsPerPageChange = {(e) => setRowsPerPage(+e.target.value)}
                        />
                    </CardContent>
                </StyledCard>}
                {openWallet && !transaction_type && <Wallet bankAccounts={summary?.accounts || {}}/>}
            </Grid2>
        </DashboardBody>
    </>;
}

const Summary = ({loading, summary, type}: any) => {

    return <>
        <Grid2 container spacing={2} sx={{margin: '1rem 0'}} >
            {/* Balance */
                type == 'fund-transfer' &&
                <Grid2 size={{xs: 12, sm: 6, xl: 'grow'}}>
                    <StyledCard>
                        <CardContent>
                            <Typography>
                                Balance
                            </Typography>
                            {loading?
                                <CircularProgress size="1.5rem" />:
                                <motion.div
                                    initial    = {{ opacity: 0, y: -20 }}
                                    animate    = {{ opacity: 1, y: 0 }}
                                    transition = {{ duration: 1 }}
                                >
                                    <Typography variant="h6">
                                        {(summary.BALANCE || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                                    </Typography>
                                </motion.div>
                            }
                        </CardContent>
                    </StyledCard>
                </Grid2>
            }
            {/* Total */}
            <Grid2 size={{xs: 12, sm: 6, xl: 'grow'}}>
                <StyledCard>
                    <CardContent>
                        <Typography>
                            Total
                        </Typography>
                        {loading?
                            <CircularProgress size="1.5rem" />:
                            <motion.div
                                initial    = {{ opacity: 0, y: -20 }}
                                animate    = {{ opacity: 1, y: 0 }}
                                transition = {{ duration: 1 }}
                            >
                                <Typography variant="h6">
                                    {(summary.TOTAL || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                                </Typography>
                            </motion.div>
                        }
                    </CardContent>
                </StyledCard>
            </Grid2>
            {/* Total (net) */
                type == 'payment' &&
                <Grid2 size={{xs: 12, sm: 6, xl: 'grow'}}>
                    <StyledCard>
                        <CardContent>
                            <Typography>
                                Total (net)
                            </Typography>
                            {loading?
                                <CircularProgress size="1.5rem" />:
                                <motion.div
                                    initial    = {{ opacity: 0, y: -20 }}
                                    animate    = {{ opacity: 1, y: 0 }}
                                    transition = {{ duration: 1 }}
                                >
                                    <Typography variant="h6">
                                        {({
                                            "payment"      : +(summary.TOTAL || 0) - +(summary.TOTAL || 0)*(summary?.fees?.CASHIN?.fee_value || 0)/100,
                                            "fund-transfer": +(summary.TOTAL || 0) + +(summary.COUNT || 0)*(summary?.fees?.CASHOUT?.fee_value || 0)
                                        }[type as string] || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                                    </Typography>
                                </motion.div>
                            }
                        </CardContent>
                    </StyledCard>
                </Grid2>
            }
            {/* Transactions */}
            <Grid2 size={{xs: 12, sm: 6, lg: 'grow'}}>
                <StyledCard>
                    <CardContent>
                        <Typography>
                            Transactions
                        </Typography>
                        {loading?
                            <CircularProgress size="1.5rem" />:
                            <motion.div
                                initial    = {{ opacity: 0, y: -20 }}
                                animate    = {{ opacity: 1, y: 0 }}
                                transition = {{ duration: 1 }}
                            >
                                <Typography variant="h6">
                                    {(summary.COUNT || 0).toLocaleString(undefined, {maximumFractionDigits: 0})}
                                </Typography>
                            </motion.div>
                        }
                    </CardContent>
                </StyledCard>
            </Grid2>
            {/* Transaction Per Minute */}
            <Grid2 size={{xs: 12, sm: 6, lg: 'grow'}}>
                <StyledCard>
                    <CardContent>
                        <Typography>
                            Trx/Min
                        </Typography>
                        {loading?
                            <CircularProgress size="1.5rem" />:
                            <motion.div
                                initial    = {{ opacity: 0, y: -20 }}
                                animate    = {{ opacity: 1, y: 0 }}
                                transition = {{ duration: 1 }}
                            >
                                <Typography variant="h6">
                                    {(summary.TRX_PER_MIN || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                                </Typography>
                            </motion.div>
                        }
                    </CardContent>
                </StyledCard>
            </Grid2>
            {/* Success Rate */}
            <Grid2 size={{xs: 12, sm: 6, lg: 'grow'}}>
                <StyledCard>
                    <CardContent>
                        <Typography whiteSpace="nowrap">
                            Success Rate
                        </Typography>
                        {loading?
                            <CircularProgress size="1.5rem" />:
                            <motion.div
                                initial    = {{ opacity: 0, y: -20 }}
                                animate    = {{ opacity: 1, y: 0 }}
                                transition = {{ duration: 1 }}
                            >
                                <Typography variant="h6">
                                    {(
                                        (summary.SUCCESS || 0)/(summary.COUNT || 1)*100
                                    ).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}%
                                </Typography>
                            </motion.div>
                        }
                    </CardContent>
                </StyledCard>
            </Grid2>
                {/* Success */}
                <Grid2 size={{xs: 12, sm: 6, lg: 'grow'}}>
                    <StyledCard>
                        <CardContent>
                            <Typography>
                                Success
                            </Typography>
                            {loading?
                                <CircularProgress size="1.5rem" />:
                                <motion.div
                                    initial    = {{ opacity: 0, y: -20 }}
                                    animate    = {{ opacity: 1, y: 0 }}
                                    transition = {{ duration: 1 }}
                                >
                                    <Typography variant="h6">
                                        {(summary.SUCCESS || 0).toLocaleString(undefined, {maximumFractionDigits: 0})}
                                    </Typography>
                                </motion.div>
                            }
                        </CardContent>
                    </StyledCard>
                </Grid2>
                {/* Pending */}
                <Grid2 size={{xs: 12, sm: 6, lg: 'grow'}}>
                    <StyledCard>
                        <CardContent>
                            <Typography>
                                Pending
                            </Typography>
                            {loading?
                                <CircularProgress size="1.5rem" />:
                                <motion.div
                                    initial    = {{ opacity: 0, y: -20 }}
                                    animate    = {{ opacity: 1, y: 0 }}
                                    transition = {{ duration: 1 }}
                                >
                                    <Typography variant="h6">
                                        {(summary.PENDING || 0).toLocaleString(undefined, {maximumFractionDigits: 0})}
                                    </Typography>
                                </motion.div>
                            }
                        </CardContent>
                    </StyledCard>
                </Grid2>
                {/* Closed */}
                <Grid2 size={{xs: 12, sm: 6, lg: 'grow'}}>
                    <StyledCard>
                        <CardContent>
                            <Typography>
                                Closed
                            </Typography>
                            {loading?
                                <CircularProgress size="1.5rem" />:
                                <motion.div
                                    initial    = {{ opacity: 0, y: -20 }}
                                    animate    = {{ opacity: 1, y: 0 }}
                                    transition = {{ duration: 1 }}
                                >
                                    <Typography variant="h6">
                                        {(summary.CLOSED || 0).toLocaleString(undefined, {maximumFractionDigits: 0})}
                                    </Typography>
                                </motion.div>
                            }
                        </CardContent>
                    </StyledCard>
                </Grid2>
                {/* Failed */}
                <Grid2 size={{xs: 12, sm: 6, lg: 'grow'}}>
                    <StyledCard>
                        <CardContent>
                            <Typography>
                                Failed
                            </Typography>
                            {loading?
                                <CircularProgress size="1.5rem" />:
                                <motion.div
                                    initial    = {{ opacity: 0, y: -20 }}
                                    animate    = {{ opacity: 1, y: 0 }}
                                    transition = {{ duration: 1 }}
                                >
                                    <Typography variant="h6">
                                        {(summary.FAILED || 0).toLocaleString(undefined, {maximumFractionDigits: 0})}
                                    </Typography>
                                </motion.div>
                            }
                        </CardContent>
                    </StyledCard>
                </Grid2>
        </Grid2>
    </>
}

const ResendButton = ({id}: any) => {
    const [loading, setLoading] = useState(false);
    const [sent, setSent] = useState(false);

    const resend = () => {
        setLoading(true)
        fetch(`${window.location.protocol}//${BASE_URL}/api/qrph/webhook/send/${id}`, {method: 'POST'})
        .then(res => res.status === 200? Swal.fire({
            title: 'Webhook Sent',
            icon : 'success'
        }).then(() => setSent(true)): null)
        .finally(() => setLoading(false))
    }

    return (
        <StyledButton variant="contained" onClick={resend} disabled={loading || sent}>
            {loading? <CircularProgress size="24px"/>: "Resend Webhook"}
        </StyledButton>
    )
}

const ViewButton = ({id, amount, status}: any) => {
    const [loading, setLoading] = useState(false);

    const open = () => {
        setLoading(true)
        fetch(`${window.location.protocol}//${BASE_URL}/dashboard/bank-details/${id}`, {method: 'GET'})
        .then(res => res.json())
        .then(res => Swal.fire({
            html: `<div style="padding: 1rem">
                <img src="\instapay.webp" width="20%">
                <div style="text-align: left">
                    <h2 style="text-align: center; color: ${{
                        SUCCESS: "green",
                        PENDING: "yellow",
                        FAILED : "red"
                    }[status as string]}">${{
                        SUCCESS: "Amount Sent",
                        PENDING: "Processing",
                        FAILED : "Failed"
                    }[status as string]}</h2>
                    <h3 style="text-align: center">₱ ${amount.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</h3>
                    <p>Account Name: ${res.account_name || ''}</p>
                    <p>Account Number: ${res.account_number || ''}</p>
                    <p>Bank/E-Wallet: ${res.bank || ''}</p>
                </div>
            </div>`
        }))
        .finally(() => {
            setLoading(false)
        })
    }

    return <>

        <StyledButton variant="contained" onClick={open} disabled={loading}>
            {loading? <CircularProgress size="24px"/>: "View"}
        </StyledButton>
    </>
}

export default Dashboard;